<template>
  <div
    class="@container cursor-pointer"
    @click="handleLinkClick($event, event._links.page)"
  >
    <app-card
      class="group relative size-full min-h-[420px] overflow-hidden bg-gray-50"
      :class="{
        'max-md:flex max-md:!min-h-0 max-md:items-start max-md:gap-4 max-md:!rounded-none':
          hasMobileLayout,
      }"
      :style="{
        width: width ? `${width}px` : undefined,
      }"
      tag="section"
    >
      <nuxt-link
        class="isolate"
        :class="{ 'max-md:relative': hasMobileLayout }"
        :to="event._links.page"
      >
        <div
          class="group-hover:scale-102 absolute inset-0 isolate size-full overflow-hidden object-cover transition duration-500"
          :class="{
            'shrink-0 max-md:static max-md:h-32 max-md:w-24': hasMobileLayout,
          }"
        >
          <app-image
            v-if="event.teacher"
            :alt="event.teacher.full_name!"
            class="size-full object-cover"
            :class="{ 'max-md:rounded': hasMobileLayout }"
            :src="
              event.options.pinned &&
              (event.course.cover_url || event.teacher.cover_url)
                ? (event.course.cover_url! ?? event.teacher.cover_url!)
                : event.teacher.avatar_url
            "
          />

          <template v-for="(teacher, idx) in event.teachers" v-else :key="idx">
            <transition
              enter-active-class="transition-opacity duration-500"
              enter-from-class="opacity-0"
              leave-active-class="transition-opacity duration-500"
              leave-to-class="opacity-0"
            >
              <app-image
                v-show="currentImageIndex === idx"
                :alt="teacher.full_name!"
                class="absolute inset-0 size-full object-cover"
                :class="{
                  'z-10': currentImageIndex === idx,
                  'max-md:rounded': hasMobileLayout,
                }"
                :src="
                  event.options.pinned && teacher.cover_url
                    ? teacher.cover_url
                    : teacher.avatar_url
                "
              />
            </transition>
          </template>
        </div>
      </nuxt-link>

      <span
        class="absolute size-full bg-gradient-to-t from-black/70 from-20% to-transparent"
        :class="{ 'max-md:hidden': hasMobileLayout }"
      />

      <nuxt-link
        class="@xl:p-10 relative flex size-full flex-col p-4 text-white transition duration-300 group-hover:translate-y-2 group-hover:opacity-0"
        :class="{
          'max-md:items-start max-md:!p-0 max-md:text-black max-md:group-hover:translate-y-0 max-md:group-hover:opacity-100':
            hasMobileLayout,
        }"
        :to="event._links.page"
      >
        <span class="flex-auto" />
        <strong
          v-if="suptitle"
          class="flex items-center gap-1 text-sm"
          :class="suptitle.class"
          ><app-icon :icon="suptitle.icon" :size="20" />{{
            suptitle.label
          }}</strong
        >
        <h3
          class="font-title @xl:text-4xl/normal my-2 line-clamp-3 text-2xl/normal"
          :class="{
            'max-md:mb-0 max-md:mt-0 max-md:line-clamp-1 max-md:font-sans max-md:!text-xl/normal':
              hasMobileLayout,
          }"
        >
          {{ event.course.subject || event.course.title }}
        </h3>
        <p class="@xl:text-lg text-sm font-semibold">
          {{ event.teacher ? event.teacher.full_name : teachersListLabel }}
        </p>
        <p v-if="event.teacher" class="line-clamp-1 text-sm">
          {{ event.teacher.job }}
        </p>
        <p class="mt-2">
          <teacher-companies-logos
            :companies="teachersCompanieLogos"
            :height="28"
          />
        </p>

        <event-type-chip
          class="@xl:h-8 @xl:text-base @xl:px-2 @xl:[&_svg]:size-5 @xl:[&_svg]:mr-1 !absolute left-4 top-4"
          :class="{
            '!border-white !bg-black/10 !text-white': !event.is_upcoming,
            'max-md:hidden': hasMobileLayout,
          }"
          :event="event"
          size="small"
        />
      </nuxt-link>

      <div
        class="pointer-coarse:hidden background-blend-color @xl:p-10 absolute inset-0 p-4 opacity-0 backdrop-blur-lg transition duration-300 group-hover:opacity-100"
        :class="{ 'max-md:!hidden': hasMobileLayout }"
        :style="{ backgroundColor: overlayColor }"
      >
        <div
          class="flex h-full translate-y-2 flex-col justify-end text-white transition duration-300 group-hover:translate-y-0"
        >
          <strong
            v-if="suptitle"
            class="mb-2 flex items-center gap-1 text-sm"
            :class="suptitle.class"
          >
            <app-icon :icon="suptitle.icon" :size="20" />{{ suptitle.label }}
          </strong>

          <p class="font-title @xl:text-2xl/normal mb-8 text-xl/normal">
            {{ event.course.title }}
          </p>
          <div class="flex flex-col gap-3">
            <event-application-cta
              v-if="event.is_upcoming"
              color="light"
              v-bind="ctaBindings"
              size="lg"
              @click.stop
            />

            <app-button
              block
              color="light"
              :icon="pageCta.icon"
              size="lg"
              :to="event._links.page"
              :variant="event.is_upcoming ? 'outlined' : undefined"
              @click.stop
            >
              {{ pageCta.label }}
            </app-button>
          </div>
        </div>
      </div>
    </app-card>
  </div>
</template>

<script lang="ts" setup>
import { isSameYear } from "date-fns";

import {
  type CarouselProvidedState,
  CarouselProvideKey,
} from "~/shared/carousel/carousel.model";

const properties = defineProps<{
  event: EventListView;
  mobileLayout?: boolean;
}>();

const carouselContext = inject<CarouselProvidedState | null>(
  CarouselProvideKey,
  null,
);

const width = computed(() => carouselContext?.itemWidth);

const startDate = useI18nDate(
  properties.event.start_date!,
  isSameYear(new Date(), new Date(properties.event.start_date!))
    ? {
        day: "numeric",
        month: "long",
      }
    : {
        timeZone: properties.event.timezone,
        day: "numeric",
        month: "short",
        year: "numeric",
      },
);

const suptitle = computed<
  { icon: string; label: string; class?: string } | undefined
>(() => {
  if (properties.event.is_upcoming) {
    return {
      label: startDate.value,
      icon: "ph:calendar-blank",
    };
  }

  if (properties.event.has_replay) {
    return {
      label: "Replay dispo",
      icon: "ph:repeat",
      class: "text-xs",
    };
  }

  return {
    label: "Résumé dispo",
    icon: "ph:article",
    class: "text-xs",
  };
});

const pageCta = computed(() => {
  if (properties.event.is_upcoming) return { label: "Voir le détail" };
  if (properties.event.has_replay)
    return { label: "Voir le replay", icon: "ph:video" };
  return { label: "Lire le résumé", icon: "ph:article" };
});

const definition = useEventType(properties.event.options.session_type);
const overlayColor = hexToHSLA(definition.color, {
  luminance: 40,
  saturation: 50,
  alpha: 30,
});

const { ctaBindings } = useEventApplication(properties.event);

const hasMobileLayout = computed(() => {
  return !carouselContext && !properties.event.options.pinned;
});

const currentImageIndex = ref(0);

useIntervalFn(() => {
  if (properties.event.teachers.length <= 1) return;
  currentImageIndex.value =
    (currentImageIndex.value + 1) % properties.event.teachers.length;
}, 5000);

const teachersListLabel = useI18nList(
  properties.event.teachers.map((teacher) => teacher.full_name!),
);
const teachersCompanieLogos = computed(() => {
  if (properties.event.teacher) {
    return properties.event.teacher.companies;
  }

  return properties.event.teachers
    .map((teacher) => teacher.companies[0])
    .filter(Boolean);
});
</script>
